import React, { useEffect, useState, useMemo, useContext } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Input } from '../../components/ui/input';
import { Separator } from '../../components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "../../components/ui/pagination";
import axios from 'axios';
import Chat from './Chat';
import { Button } from '../../components/ui/button';
import { Search } from 'lucide-react';
import { toast, Toaster } from "sonner";
import { ApiContext } from '../context/AdminContext';

// Simple CSS-based spinner
const Spinner = () => (
  <div className="inline-block w-5 h-5 border-4 border-t-transparent border-gray-600 rounded-full animate-spin mr-2"></div>
);

const Booking = () => {
  const token = localStorage.getItem("adminToken");
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [selectedPartnerId, setSelectedPartnerId] = useState("");
  const [isAssigning, setIsAssigning] = useState(false);
  const [partnerSearchTerm, setPartnerSearchTerm] = useState("");
  const { partners } = useContext(ApiContext);

  const approvedPartners = useMemo(() => {
    return partners?.filter((partner) => partner?.Profile?.KYC?.status === 'approved') || [];
  }, [partners]);

  // Filter partners based on partner search term
  const filteredPartners = useMemo(() => {
    if (!partnerSearchTerm) return approvedPartners;

    const lowercasedSearch = partnerSearchTerm.toLowerCase();
    return approvedPartners.filter(partner =>
      partner?.Profile?.name?.toLowerCase().includes(lowercasedSearch) ||
      partner?.Profile?.phone?.includes(partnerSearchTerm)
    );
  }, [approvedPartners, partnerSearchTerm]);

  // Fetch bookings data
  useEffect(() => {
    const fetchBookings = async () => {
      setIsLoading(true);
      try {
        const res = await axios.get("https://wavetechservices.in/admin/bookings", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setBookings(res.data.data);
      } catch (err) {
        console.error("Error fetching bookings:", err);
        toast.error("Failed to fetch bookings");
      } finally {
        setIsLoading(false);
      }
    };
    fetchBookings();
  }, [token]);

  // Filter bookings based on search term

  console.log("booking",bookings)
  const filteredBookings = useMemo(() => {
    if (!searchTerm) return bookings;

    const lowercasedSearch = searchTerm.toLowerCase();
    return bookings.filter(booking =>
      booking.customerName?.toLowerCase().includes(lowercasedSearch) ||
      booking.customerPhone?.includes(searchTerm) ||
      booking.serviceName?.toLowerCase().includes(lowercasedSearch) ||
      booking.partnerName?.toLowerCase().includes(lowercasedSearch) ||
      booking.status?.toLowerCase().includes(lowercasedSearch) ||
      booking.paymentMode?.toLowerCase().includes(lowercasedSearch) ||
      booking.location?.address?.toLowerCase().includes(lowercasedSearch) ||
      booking.location?.pincode?.includes(searchTerm)
    );
  }, [bookings, searchTerm]);

  // Pagination
  const itemsPerPage = 5;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredBookings.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Format address
  const formatAddress = (location) => {
    if (!location) return "N/A";
    return `${location.address || ''} ${location.landmark !== "N/A" ? location.landmark : ''} ${location.pincode !== "N/A" ? location.pincode : ''}`.trim();
  };

  // Handle partner selection
  const handleSelectPartner = (partnerId) => {
    if(!partnerId) return
    setSelectedPartnerId(partnerId);
    console.log("Selected Partner ID:", partnerId); // Debugging
  };

  // Handle partner assignment
  const handleAssignPartner = async () => {
    if (!selectedPartnerId) {
      toast.error("Please select a partner");
      return;
    }

    setIsAssigning(true);
    try {
      console.log("Assigning partner with ID:", selectedPartnerId, "to booking:", selectedBookingId); // Debugging
      await axios.put(
        `https://wavetechservices.in/api/admin/bookings/assign-partner`,
        {
          bookingId: selectedBookingId,
          partnerId: selectedPartnerId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success("Partner assigned successfully");
      setOpenAssignDialog(false);
      setSelectedPartnerId("");
      setSelectedBookingId(null);
      setPartnerSearchTerm("");
      // Refresh bookings
      const res = await axios.get("https://wavetechservices.in/admin/bookings", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setBookings(res.data.data);
    } catch (error) {
      console.error("Error assigning partner:", error.response?.data || error.message);
      toast.error(error.response?.data?.message || "Failed to assign partner");
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <div className='px-[1vw] my-5'>
      <Toaster />
      <div className='flex lg:justify-between lg:items-center flex-col lg:flex-row'>
        <h1 className='text-2xl'>Bookings ({filteredBookings.length})</h1>
        <div className='flex lg:gap-4 items-center gap-2 my-3 lg:my-0'>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder='Search bookings...'
              className="pl-10 w-full lg:w-64"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>
      <Separator className='my-4' />

      <div className='overflow-x-auto lg:max-w-full h-[65vh]'>
        {isLoading ? (
          <div className="flex items-center justify-center h-full gap-2">
            <Spinner />
            <p>Loading bookings...</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className='w-[50px]'>Sl No.</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead className="min-w-[200px]">Location</TableHead>
                <TableHead>Service</TableHead>
                 <TableHead>Sub Service</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Provider Number</TableHead>
                <TableHead>Chat</TableHead>
                <TableHead>Schedule</TableHead>
                <TableHead>Booked On</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {currentItems.length > 0 ? (
                currentItems.map((booking, ind) => (
                  <TableRow key={booking._id}>
                    <TableCell>{indexOfFirstItem + ind + 1}.</TableCell>
                    <TableCell className="font-medium">{booking.customerName || "N/A"}</TableCell>
                    <TableCell>{booking.customerPhone || "N/A"}</TableCell>
                    <TableCell>{formatAddress(booking.location)}</TableCell>
                    <TableCell>{booking.serviceName || "N/A"}</TableCell>
                      <TableCell>{booking?.booking?.subService?.name || "N/A"}</TableCell>
                    <TableCell>₹{booking.amount || "0"}</TableCell>
                    <TableCell className="capitalize">{booking.paymentMode || "N/A"}</TableCell>
                    <TableCell>{booking.partnerName || "N/A"}</TableCell>
                    <TableCell>{booking?.partner?.phone || "N/A"}</TableCell>
                    <TableCell>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="ghost" size="sm">
                            Chat
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                          <DialogHeader>
                            <DialogTitle>Chat with {booking.partnerName || "Service Provider"}</DialogTitle>
                            <DialogDescription>
                              <Chat booking={booking} />
                            </DialogDescription>
                          </DialogHeader>
                        </DialogContent>
                      </Dialog>
                    </TableCell>
                    <TableCell>
                      {new Date(booking.scheduledDate).toLocaleDateString()} / {booking.scheduledTime} {booking?.booking?.currentBooking && "Instant Booking"}
                    </TableCell>
                    <TableCell>{new Date(booking.createdAt).toLocaleString()}</TableCell>
                    <TableCell className="capitalize">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        booking.status === 'completed' ? 'bg-green-100 text-green-800' :
                        booking.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {booking.status || "N/A"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Dialog
                        open={openAssignDialog && selectedBookingId === booking._id}
                        onOpenChange={(open) => {
                          setOpenAssignDialog(open);
                          if (!open) {
                            setSelectedPartnerId("");
                            setSelectedBookingId(null);
                            setPartnerSearchTerm("");
                          }
                        }}
                      >
                        <DialogTrigger asChild>
                          { booking.status === 'completed' ||booking.status === 'cancelled' ? 
                          <Button
                            variant="outline"
                            size="sm"
                           disabled={true}
                          >
                            Service Closed
                          </Button>:<Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedBookingId(booking._id);
                              setOpenAssignDialog(true);
                            }}
                          >
                            Assign
                          </Button>}
                          
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-[625px]">
                          <DialogHeader>
                            <DialogTitle className="lg:text-2xl text-xl">
                              Assign Partner
                            </DialogTitle>
                          </DialogHeader>
                          <div className="flex flex-col py-4 space-y-3">
                            <div className="flex flex-col gap-4">
                              <div className="flex items-center gap-4">
                                <Label htmlFor="partnerSearch" className="w-44">
                                  Search Partner
                                </Label>
                                <div className="relative flex-1">
                                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                  <Input
                                    id="partnerSearch"
                                    placeholder="Search by name or phone..."
                                    className="pl-10"
                                    value={partnerSearchTerm}
                                    onChange={(e) => setPartnerSearchTerm(e.target.value)}
                                  />
                                </div>
                              </div>
                              <div className="max-h-[200px] overflow-y-auto border rounded-md p-2 z-2">
                                {filteredPartners.length > 0 ? (
                                  filteredPartners.map((partner) => (
                                    <div
                                      key={partner?.Profile?.id}
                                      className={`p-2 cursor-pointer hover:bg-gray-100 rounded-md flex justify-between items-center ${
                                        selectedPartnerId === partner?.Profile?.id ? 'bg-gray-200 font-semibold' : ''
                                      }`}
                                      onClick={() => handleSelectPartner(partner?.Profile?.id)}
                                    >
                                      <span>{partner?.Profile?.name} ({partner?.Profile?.phone})</span>
                                      {selectedPartnerId === partner?.Profile?.id && (
                                        <span className="text-green-600">✓ Selected</span>
                                      )}
                                    </div>
                                  ))
                                ) : (
                                  <div className="p-2 text-gray-500">No partners found</div>
                                )}
                              </div>
                            </div>
                          </div>
                          <DialogFooter>
                            <Button
                              type="submit"
                              className="bg-black px-5 text-white h-9 hover:opacity-90 w-full flex items-center justify-center gap-2"
                              onClick={handleAssignPartner}
                              disabled={isAssigning || !selectedPartnerId}
                            >
                              {isAssigning ? (
                                <>
                                  <Spinner />
                                  Assigning...
                                </>
                              ) : (
                                "Assign"
                              )}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={14} className="text-center py-10">
                    {searchTerm ? "No matching bookings found" : "No bookings available"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>

      {filteredBookings.length > itemsPerPage && (
        <div className="mt-4">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                />
              </PaginationItem>

              {Array.from({ length: totalPages }).map((_, index) => {
                const page = index + 1;
                if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                  return (
                    <PaginationItem key={page}>
                      <PaginationLink
                        isActive={page === currentPage}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  );
                }
                return null;
              })}

              <PaginationItem>
                <PaginationNext
                  onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default Booking;